import { Box, Typography, useTheme } from "@mui/material";
import { GroupCardData } from "./types";
import { useTranslation } from "react-i18next";
import { Diamond, GraduationCap, Trash2, Users } from "lucide-react";
import { formatDate } from "@utils/utilsDate";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import MoreActionsMenuCst from "@components/MoreActionsMenuCst";

export const GroupCard: React.FC<{
  group: GroupCardData;
  onDetail: () => void;
  onDeleteEnrollment?: () => void;
  canDeleteEnrollment?: boolean;
}> = ({ group, onDetail, onDeleteEnrollment, canDeleteEnrollment = true }) => {
  const theme = useTheme();
  const { t } = useTranslation("user-detail-v2");

  const metrics = [
    {
      icon: <Diamond size={16} />,
      value: group.initiatives ?? 0,
      label: t("groups.initiatives"),
    },
    {
      icon: <Users size={16} />,
      value: group.enrolledUsers ?? 0,
      label: t("groups.enrolledUsers"),
    },
    {
      icon: <GraduationCap size={16} />,
      value: group.learningObjects ?? 0,
      label: t("groups.learningObjects"),
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        backgroundColor: theme.palette.action.hover,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "10px",
        p: "1rem",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
            {group.name}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: theme.palette.text.primary,
              fontStyle: "regular",
            }}
          >
            ID: {group.id} |{" "}
            {group.type === "STATIC"
              ? t("groups.typeStatic")
              : t("groups.typeDynamic")}
          </Typography>
        </Box>
        {group.createdAt && (
          <Box
            sx={{
              backgroundColor: theme.palette.text.primary,
              color: theme.palette.background.default,
              borderRadius: "0.25rem",
              height: "1.5625rem",
              paddingX: "0.5rem",
              paddingTop: "0.15rem",
              paddingBottom: "0.1rem",
              fontSize: "0.75rem",
              fontWeight: 600,
              whiteSpace: "nowrap",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            {t("groups.createdOn")} {formatDate(group.createdAt)}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "end",
            gap: 3,
            flexWrap: "wrap",
            color: theme.palette.text.primary,
          }}
        >
          {/* TODO waiting for BE data */}
          {/* {metrics.map((m, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "end", gap: 0.75 }}>
              {m.icon}
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  lineHeight: "1rem",
                  fontWeight: 400,
                }}
              >
                {m.value} {m.label}
              </Typography>
            </Box>
          ))} */}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <ButtonSecondary onClick={onDetail}>
            {t("groups.details")}
          </ButtonSecondary>
          {group.type === "STATIC" && onDeleteEnrollment && (
            <MoreActionsMenuCst
              ariaLabel={t("groups.moreActions")}
              actions={[
                {
                  label: t("groups.delete"),
                  icon: <Trash2 size={16} />,
                  onClick: onDeleteEnrollment,
                  disabled: !canDeleteEnrollment,
                },
              ]}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
